import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import { AssessmentResultType } from 'types/Assessment';
import { RootState } from 'redux/store';
import {
  getCandidateInfo,
  getAllCandidateData,
  getCandidateAssessmentResults,
  patchCandidateMainInfoData,
  selectSkillsError,
  selectSkillsStreaming,
  selectSkillsProgress
} from 'redux/slices/candidate';
import Page from 'components/containers/Page';
import LoadingScreen from 'components/LoadingScreen';
import useLocales from 'hooks/useLocales';
import { getPlaceholders } from 'constants/text/placeholders';
import { splitToColumns } from 'helpers/listValues';
import { PATH_HOME } from 'routes/paths';
import axios from 'utils/axios';
import { Grid } from '@material-ui/core';
import DashboardNavbar from 'layouts/dashboard/navbar/DashboardNavbar';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { Title1, TitlePrimary } from 'components/text/title';
import { useSnackbar } from 'notistack';
import useAuth from '@hooks/useAuth';
import AIModal from 'components/AIModal';
import { CandidateAssessmentGraph } from './CandidateAssessmentGraph';
import ProfileHeader from './ProfileHeader';
import ColumnedList from './ColumnedList';
import { CandidateAssessmentForPDF } from './CandidateAssessmentFroPDF';
import ProfileHeaderPDF from './ProfileHeaderPDF';
import { CandidateProfileProps } from '../../../types/Candidate';
import { getCustomer, getCustomerFiles } from '../../../redux/slices/customrs';
import { useCandidateProfileCards } from './CandidateProfileCards';

const CandidateProfile = ({
  isForCostumerProfile
}: CandidateProfileProps): JSX.Element => {
  // All hooks at the top level - no conditional calls
  const token = localStorage.getItem('newToken');
  const [PDFType, setPDFType] = useState('all');
  const [pdfLoading, setPDFLoading] = useState(false);
  const [base64Image, setBase64Image] = useState('');
  const [currentGroups, setCurrentGroups] = useState<any>(null);
  const [editSkillsModalOpen, setEditSkillsModalOpen] = useState(false);
  const [isCvUploadModal, setIsCvUploadModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [openAvatarUpload, setOpenAvatarUpload] = useState(false);
  const [isMadeAnonymous, setIsMadeAnonymous] = useState<boolean>(true);

  const componentRef = useRef(null);
  const { id } = useParams<any>();
  const customerId = id;
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const { enqueueSnackbar } = useSnackbar();

  // All useSelector calls grouped together - always called in same order
  const { candidate, candidateLoading } = useSelector(
    (state: RootState) => state.candidate
  );
  const { company }: any = useSelector(
    (state: RootState) => state.generalRecruiter
  );
  const modules = useSelector(
    (state: RootState) => state.dashboard.companyModuleInsights
  );
  const userAssessmentResult = useSelector(
    (state: RootState) => state.candidate.assessmentResult
  );

  const { notes, customer, files } = useSelector(
    (state: RootState) => state.customers
  );
  const {
    assessmentResult,
    assessmentLoading,
    soft_skills,
    hard_skills
  }: {
    assessmentResult: AssessmentResultType;
    assessmentLoading: boolean;
    soft_skills: string[];
    hard_skills: string[];
  } = useSelector((state: RootState) => {
    const candidateState = state.candidate;
    return candidateState;
  });
  const skillsError = useSelector(selectSkillsError);
  const skillsStreaming = useSelector(selectSkillsStreaming);
  const skillsProgress = useSelector(selectSkillsProgress);

  // Other hooks - always called in same order
  const {
    user,
    isAdmin,
    isRecruiterPlus,
    isRecruiter,
    isHiringManager,
    isReviewer,
    isEmployee,
    isCandidate
  } = useAuth();
  const {
    t,
    currentLang: { value: lng }
  } = useLocales();
  const { ANONYMOUS } = getPlaceholders();

  // Derived values
  const isEditable = isEmployee || isCandidate;
  const candidateID = id && id !== 'me' ? id : user?.candidate_id || '';
  const userID = id && id === 'me' ? user.id : candidate?.mainInfo?.user || '';
  const result = assessmentResult?.result || null;
  const status = assessmentResult?.assessment?.status;

  // useMemo hook - always called
  // Returns all available reports: [primaryReport, secondaryReport, hrmReport, feedbackReport, moDeReport, dynamicReport]
  const [
    reportLink,
    motivatorLink,
    hrmReportLink,
    feedbackReportLink,
    moDeReportLink,
    dynamicReportLink
  ]: any = useMemo(() => {
    if (
      userAssessmentResult?.reports !== null &&
      userAssessmentResult?.reports !== undefined &&
      userAssessmentResult?.result !== null
    ) {
      const { reports } = userAssessmentResult as any;
      const { feedback, mo_de, hrm, dynamic } = reports;

      let hrmReport: string | null = null;
      let feedbackReport: string | null = null;
      let moDeReport: string | null = null;
      let dynamicReport: string | null = null;

      // Extract HRM report (new version)
      if (hrm && hrm !== null) {
        if (typeof hrm === 'string' && hrm.trim() !== '') {
          hrmReport = hrm;
        } else if (typeof hrm === 'object') {
          hrmReport = (hrm as any)[lng] || hrm;
        }
      }

      // Extract old version reports (feedback, mo_de, and dynamic)
      if (feedback && feedback !== null) {
        if (typeof feedback === 'object') {
          feedbackReport = (feedback as any)[lng] || feedback;
        } else if (typeof feedback === 'string' && feedback.trim() !== '') {
          feedbackReport = feedback;
        }
      }

      if (mo_de && mo_de !== null) {
        if (typeof mo_de === 'object') {
          moDeReport = (mo_de as any)[lng] || mo_de;
        } else if (typeof mo_de === 'string' && mo_de.trim() !== '') {
          moDeReport = mo_de;
        }
      }

      if (dynamic && dynamic !== null) {
        if (typeof dynamic === 'object') {
          dynamicReport = (dynamic as any)[lng] || dynamic;
        } else if (typeof dynamic === 'string' && dynamic.trim() !== '') {
          dynamicReport = dynamic;
        }
      }

      // Prioritize HRM report for primary, but keep old reports available
      const primaryReport = hrmReport || feedbackReport || dynamicReport;
      const secondaryReport = hrmReport
        ? feedbackReport || moDeReport || dynamicReport
        : moDeReport || dynamicReport;

      return [
        primaryReport,
        secondaryReport,
        hrmReport,
        feedbackReport,
        moDeReport,
        dynamicReport
      ];
    }
    return [null, null, null, null, null, null];
  }, [userAssessmentResult, lng]);

  // All useEffect hooks grouped together - always called in same order
  useEffect(() => {
    const waitingForAssessmentResults = result == null && status === 2;

    const fetchAssessmentResults = () => {
      if (waitingForAssessmentResults && userID !== '') {
        dispatch(getCandidateAssessmentResults(userID));
      }
    };

    fetchAssessmentResults();

    const interval = setInterval(() => {
      if (waitingForAssessmentResults) {
        fetchAssessmentResults();
      } else {
        clearInterval(interval);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [dispatch, userID, result, status]);

  useEffect(() => {
    if (customerId && isForCostumerProfile) {
      dispatch(getCustomer(customerId));
      if (!token || (token && user?.id)) {
        dispatch(getCustomerFiles(customerId));
      }
    }
  }, [customerId, isForCostumerProfile, dispatch, token, user?.id]);

  useEffect(() => {
    if (result) {
      const currents = result ? { ...(result as any) } : null;
      setCurrentGroups(currents);
    }
  }, [result]);

  useEffect(() => {
    if (candidateID && modules && !isForCostumerProfile && !isEmployee) {
      dispatch(getAllCandidateData(candidateID, modules));
    }
  }, [dispatch, candidateID, modules, isForCostumerProfile, isEmployee]);

  useEffect(() => {
    if (userID) {
      dispatch(getCandidateAssessmentResults(userID));
    }
  }, [dispatch, userID]);

  useEffect(() => {
    const getBase64 = async (url: string) => {
      // Method 1: Try using img element + canvas (works if S3 has CORS for img tags)
      const imgToBase64 = (): Promise<string> =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          let timeoutId: NodeJS.Timeout;
          let resolved = false;

          const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
            resolved = true;
          };

          img.onload = () => {
            if (resolved) return;
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0);
                // This will throw if canvas is tainted due to CORS issues
                const base64 = canvas.toDataURL('image/jpeg', 0.9);
                cleanup();
                resolve(base64);
              } else {
                cleanup();
                reject(new Error('Could not get canvas context'));
              }
            } catch (error: any) {
              cleanup();
              // Canvas taint error indicates CORS issue
              const isCorsError =
                error?.message?.includes('tainted') ||
                error?.name === 'SecurityError' ||
                error?.code === 18;
              reject(
                new Error(
                  isCorsError
                    ? 'CORS: Canvas tainted - S3 CORS not configured'
                    : error?.message || 'Canvas conversion failed'
                )
              );
            }
          };

          img.onerror = (error) => {
            if (resolved) return;
            cleanup();
            reject(error);
          };

          // Add a timeout to prevent hanging
          timeoutId = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              img.src = ''; // Cancel image loading
              reject(new Error('Image load timeout'));
            }
          }, 10000);

          img.src = url;
        });

      // Method 2: Try using axios (original method)
      const axiosToBase64 = async (): Promise<string> => {
        const response = await axios.get(url, {
          responseType: 'blob'
        });
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(response.data);
        });
      };

      // Try img+canvas first (often works even when axios doesn't due to CORS)
      try {
        const base64 = await imgToBase64();
        setBase64Image(base64);
      } catch (imgError) {
        // If img+canvas fails, try axios as fallback
        try {
          const base64 = await axiosToBase64();
          setBase64Image(base64);
        } catch (axiosError) {
          // If both fail, log the error but don't break the app
          // The component will use the original URL as fallback
          console.warn(
            'Could not convert image to base64 due to CORS restrictions. Using original URL instead.',
            {
              imgError,
              axiosError
            }
          );
          // Set empty string so the component can use the original URL
          setBase64Image('');
        }
      }
    };

    const candidateAvatar =
      id === 'me' ? user.avatar : candidate?.mainInfo?.avatar || '';
    if (candidateAvatar) {
      getBase64(candidateAvatar);
    }
  }, [candidate?.mainInfo?.avatar, id, user.avatar]);

  // useEffect(() => {
  //   if (openAvatarUpload) {
  //     setEditSkillsModalOpen(false);
  //   }
  //   if (editSkillsModalOpen) {
  //     setOpenAvatarUpload(false);
  //   }
  // }, [openAvatarUpload, editSkillsModalOpen]);

  const candidateName =
    candidate?.mainInfo?.anonymous && !isEditable
      ? ANONYMOUS
      : candidate?.mainInfo?.name;

  const candidateBannerImage = company.candidate_banner;

  const recruiterOrAdmin =
    isAdmin || isRecruiterPlus || isRecruiter || isHiringManager || isReviewer;

  const isCandidateProfile =
    pathname === PATH_HOME.dashboardCandidate && (isCandidate || isEmployee);

  const handleAnonymous = () => {
    dispatch(
      patchCandidateMainInfoData(candidateID, {
        anonymous: !candidate?.mainInfo?.anonymous
      })
    );
  };

  const handleSkillsModalClose = () => {
    setEditSkillsModalOpen(false);
    setIsCvUploadModal(false);
  };

  const handleSkillsModalToggle = (open: boolean) => {
    if (open) {
      setEditSkillsModalOpen(true);
    } else {
      handleSkillsModalClose();
    }
  };
  const uploadCV = async (
    formData: FormData,
    onProgress?: (skills: any) => void
  ) => {
    dispatch(patchCandidateMainInfoData(candidateID, formData, onProgress));
  };

  const handleUploadCV = async (file: any) => {
    const formData = new FormData();
    if (file) formData.append(`cv`, file);
    setShowAIModal(true);

    try {
      await uploadCV(formData, (skills) => {
        // Real-time skills updates are handled by Redux
      });

      // Wait for streaming to complete
      const checkStreaming = () => {
        if (!skillsStreaming) {
          if (skillsError !== null) {
            enqueueSnackbar(t(skillsError), {
              variant: 'error'
            });
            setEditSkillsModalOpen(false);
          } else {
            setIsCvUploadModal(true);
            setEditSkillsModalOpen(true);
          }
          setShowAIModal(false);
        } else {
          // Still streaming, check again in 100ms
          setTimeout(checkStreaming, 100);
        }
      };

      checkStreaming();
    } catch (error: any) {
      enqueueSnackbar(error || t('Failed to fetch skills from uploaded CV.'), {
        variant: 'error'
      });
      setEditSkillsModalOpen(false);
      setShowAIModal(false);
    } finally {
      dispatch(getCandidateInfo(candidateID));
    }
  };

  const createOnInit = (dispatchAction: any, condition?: boolean) => () => {
    if (!isEmployee) {
      if (condition) {
        dispatch(dispatchAction(candidateID));
        return;
      }
      dispatch(dispatchAction(candidateID));
    }
  };

  // Use the refactored hook for card rendering
  const { cards, cardsCount, workExperiences } = useCandidateProfileCards({
    token,
    isForCostumerProfile,
    isEmployee,
    isCandidate,
    isRecruiter,
    isAdmin,
    recruiterOrAdmin,
    isCandidateProfile,
    isMadeAnonymous,
    candidate,
    customer,
    notes,
    files,
    modules,
    company,
    candidateID,
    customerId,
    t,
    isEditable,
    editSkillsModalOpen,
    handleSkillsModalToggle,
    skillsStreaming,
    skillsProgress,
    isCvUploadModal,
    hard_skills,
    soft_skills,
    createOnInit
  });

  const cardsToRender = isEmployee ? [] : cards;

  const columns = splitToColumns(cardsToRender);

  if (candidateLoading) return <LoadingScreen />;

  const downloadPDFFile = async (type: string) => {
    setPDFType(type);
    setPDFLoading(true);
    setTimeout(async () => {
      const elements: any = componentRef.current;
      const asyncFunctions = [];
      for (let i = 0; i < elements.children.length; i++) {
        const element = elements.children[i];
        if (elements.children[i].children.length) {
          asyncFunctions.push(html2canvas(element));
        }
      }
      const results = await Promise.all(asyncFunctions);
      const pdf = new JsPDF();
      for (let i = 0; i < results.length; i++) {
        const canvas = results[i];
        const data = canvas.toDataURL('image/jpeg', 0.6);
        const imgProperties = pdf.getImageProperties(data);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight =
          (imgProperties.height * pdfWidth) / imgProperties.width;
        if (i) pdf.addPage();
        pdf.addImage(data, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }
      pdf.save(`${!isMadeAnonymous ? candidateName : 'Anonymous'}.pdf`);
      setPDFLoading(false);
      pdf.close();
    }, 1000);
  };

  // Group cardsCount into pairs for PDF rendering
  const arrForInfo: JSX.Element[][] = [];
  for (let i = 0; i < cardsCount.length; i += 2) {
    arrForInfo.push(cardsCount.slice(i, i + 2));
  }

  // Add workExperiences separately if it exists
  if (workExperiences) {
    arrForInfo.push([workExperiences]);
  }

  // Generate page indices for PDF pagination
  const arrayForContent = Array.from(
    { length: Math.ceil(arrForInfo.length / 2) },
    (_, i) => i
  );

  let titleNavbar;

  if (isForCostumerProfile) {
    titleNavbar = 'Customer Profile';
  } else if (recruiterOrAdmin) {
    titleNavbar = 'Candidate Profile';
  } else {
    titleNavbar = 'My Profile';
  }

  return (
    <>
      <AIModal
        open={showAIModal}
        onClose={() => setShowAIModal(false)}
        customMessage={
          skillsStreaming
            ? `${t('NORA is extracting the skills from your CV')}... ${
                skillsProgress.extracted
              }/${skillsProgress.total}`
            : t('NORA is extracting the skills from your CV')
        }
      />
      <Page
        title={`${t('Candidate')} | ${candidate?.mainInfo?.name}`}
        style={{
          background: '#ffffff'
        }}
      >
        <DashboardNavbar title={titleNavbar} />
        <ProfileHeader
          candidate={candidate}
          isEditable={isEditable}
          anonymous={candidate?.mainInfo?.anonymous}
          candidateAvatar={
            isForCostumerProfile || candidate?.mainInfo?.anonymous
              ? '/static/favicon/icon-256x256.png'
              : base64Image ||
                (id === 'me' ? user.avatar : candidate?.mainInfo?.avatar) ||
                '/static/favicon/icon-256x256.png'
          }
          candidateName={isForCostumerProfile ? customer?.name : candidateName}
          handleUploadCV={handleUploadCV}
          openAvatarUpload={openAvatarUpload}
          setOpenAvatarUpload={setOpenAvatarUpload}
          handleAnonymous={handleAnonymous}
          reportLink={reportLink}
          motivatorLink={motivatorLink}
          hrmReportLink={hrmReportLink}
          feedbackReportLink={feedbackReportLink}
          moDeReportLink={moDeReportLink}
          dynamicReportLink={dynamicReportLink}
          recruiterOrAdmin={recruiterOrAdmin}
          downloadPDFFile={downloadPDFFile}
          pdfLoading={pdfLoading}
          type={PDFType}
          isForCostumerProfile={isForCostumerProfile}
          customerId={customerId}
          isAnonymous={isMadeAnonymous}
          setIsAnonymous={(e: boolean) => setIsMadeAnonymous(e)}
          candidateBannerImage={candidateBannerImage}
        />
        <ColumnedList columns={columns} />
        <Grid
          style={{
            padding: '0 30px'
          }}
        >
          {!isForCostumerProfile && (
            <CandidateAssessmentGraph
              result={result}
              id={candidate?.mainInfo?.id}
              status={status}
              editable={isEditable}
              iconSrc="/static/img/profile/professional-skills.svg"
            />
          )}
        </Grid>
      </Page>
      {recruiterOrAdmin && (
        <div
          style={{
            height: 0,
            overflow: 'hidden'
          }}
        >
          <div
            id="divToPrint"
            style={{
              maxWidth: '1200px',
              minWidth: '1200px',
              background: '#FFFFFF'
            }}
            ref={componentRef}
          >
            {PDFType !== 'with-charts' &&
              arrayForContent.map((elem, indexY) => (
                <Grid
                  key={indexY}
                  style={{ height: '1700px', position: 'relative' }}
                >
                  <ProfileHeaderPDF
                    candidate={candidate}
                    candidateAvatar={
                      !isMadeAnonymous
                        ? base64Image ||
                          (id === 'me'
                            ? user.avatar
                            : candidate?.mainInfo?.avatar) ||
                          '/static/img/favicon/icon-192x192.png'
                        : '/static/img/favicon/icon-192x192.png'
                    }
                    candidateName={
                      !isMadeAnonymous ? candidateName : 'Anonymous'
                    }
                  />
                  {[...(PDFType !== 'with-charts' ? arrForInfo : [])]?.map(
                    (item: any, index: number) => {
                      if (index < indexY * 2 + 2 && index >= indexY * 2) {
                        return (
                          <Grid
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between'
                            }}
                            key={index}
                          >
                            {item.map((el: any, indexI: number) => (
                              <Grid
                                style={{
                                  width: item.length === 1 ? '1200px' : '400px'
                                }}
                                key={indexI}
                              >
                                {cloneElement(el, { withoutBox: true })}
                              </Grid>
                            ))}
                          </Grid>
                        );
                      }
                      return null;
                    }
                  )}
                  <Title1
                    style={{
                      textAlign: 'right',
                      position: 'absolute',
                      bottom: '20px',
                      right: '20px'
                    }}
                  >
                    {indexY + 1}
                  </Title1>
                </Grid>
              ))}
            {PDFType !== 'with-info' && currentGroups && (
              <Grid style={{ height: '1700px', position: 'relative' }}>
                <ProfileHeaderPDF
                  candidate={candidate}
                  candidateAvatar={
                    !isMadeAnonymous
                      ? base64Image
                      : '/static/img/favicon/icon-192x192.png'
                  }
                  candidateName={!isMadeAnonymous ? candidateName : 'Anonymous'}
                />
                <Title1 style={{ textAlign: 'center' }}>
                  {t('Your Personality Profile')}
                </Title1>
                <Grid
                  style={{
                    padding: '0 30px'
                  }}
                >
                  <CandidateAssessmentForPDF
                    pageCount={0}
                    repeat={false}
                    currentGroups={currentGroups}
                    assessmentLoading={assessmentLoading}
                  />
                </Grid>
                <Title1
                  style={{
                    textAlign: 'right',
                    position: 'absolute',
                    bottom: '20px',
                    right: '20px'
                  }}
                >
                  {PDFType === 'with-charts' ? 1 : arrayForContent?.length + 1}
                </Title1>
              </Grid>
            )}
            {PDFType !== 'with-info' && currentGroups && (
              <Grid style={{ height: '1700px', position: 'relative' }}>
                <ProfileHeaderPDF
                  candidate={candidate}
                  candidateAvatar={
                    !isMadeAnonymous
                      ? base64Image
                      : '/static/img/favicon/icon-192x192.png'
                  }
                  candidateName={!isMadeAnonymous ? candidateName : 'Anonymous'}
                />
                <Title1 style={{ textAlign: 'center' }}>
                  {t('Your Personality Profile')}
                </Title1>
                <Grid
                  style={{
                    padding: '0 30px'
                  }}
                >
                  <CandidateAssessmentForPDF
                    pageCount={4}
                    currentGroups={currentGroups}
                    assessmentLoading={assessmentLoading}
                  />
                </Grid>
                <TitlePrimary
                  style={{ textAlign: 'center', padding: '0 40px' }}
                >
                  {t(
                    'Explanation of scores: score 1-3 is low aptitude. 4-6 is medium aptitude. 7-8 is high aptitude. 9 is very high aptitude.'
                  )}
                </TitlePrimary>
                <Title1
                  style={{
                    textAlign: 'right',
                    position: 'absolute',
                    bottom: '20px',
                    right: '20px'
                  }}
                >
                  {PDFType === 'with-charts' ? 2 : arrayForContent?.length + 2}
                </Title1>
              </Grid>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CandidateProfile;
